import { AnyObject } from './any-object.utilities';

/** Nested diff: values in `a` that differ from `b` (undefined when identical). */
export function findNestedObjectDiff(a: unknown, b: unknown): AnyObject | undefined {
	if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
		return a !== b ? (a as AnyObject) : undefined;
	}

	if (Array.isArray(a) && Array.isArray(b)) {
		return JSON.stringify(a) !== JSON.stringify(b) ? (a as AnyObject) : undefined;
	}

	const result: AnyObject = {};
	const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);

	for (const key of keys) {
		const diff = findNestedObjectDiff((a as AnyObject)[key], (b as AnyObject)[key]);
		if (diff !== undefined) {
			result[key] = diff;
		}
	}

	return Object.keys(result).length > 0 ? result : undefined;
}

/** Flat path → `{ from, to }` summary (account history / audit diffs). */
export function findObjectDiffSummary(
	a: unknown,
	b: unknown,
	path: string[] = [],
	changed: Record<string, { from: unknown; to: unknown }> = {},
): Record<string, { from: unknown; to: unknown }> {
	if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
		if (a !== b) {
			changed[path.join('.')] = { from: a, to: b };
		}
		return changed;
	}

	if (Array.isArray(a) && Array.isArray(b)) {
		if (JSON.stringify(a) !== JSON.stringify(b)) {
			changed[path.join('.')] = { from: a, to: b };
		}
		return changed;
	}

	const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
	for (const key of keys) {
		findObjectDiffSummary((a as AnyObject)?.[key], (b as AnyObject)?.[key], [...path, key], changed);
	}

	return changed;
}

/** Flat list of dotted paths that differ (program form summaries). */
export function findObjectDiffPaths(a: unknown, b: unknown, path: string[] = [], changedPaths: string[] = []): string[] {
	if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
		if (a !== b) {
			changedPaths.push(path.join('.'));
		}
		return changedPaths;
	}

	if (Array.isArray(a) && Array.isArray(b)) {
		if (JSON.stringify(a) !== JSON.stringify(b)) {
			changedPaths.push(path.join('.'));
		}
		return changedPaths;
	}

	const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
	for (const key of keys) {
		findObjectDiffPaths((a as AnyObject)?.[key], (b as AnyObject)?.[key], [...path, key], changedPaths);
	}

	return changedPaths;
}
